/**
 * ParamInput - Reusable parameter input component
 *
 * Renders form inputs based on parameter type with styled info popover for tooltips
 */

import { useState } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Popover,
  Typography,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { useSymbols } from '../../../provider/symbolProvider';

const ParamInput = ({ param, value, onChange, accentColor = '#2563eb', symbolOptions }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { symbolNames } = useSymbols();

  const availableSymbols = symbolOptions && symbolOptions.length > 0 ? symbolOptions : symbolNames;

  const handleInfoClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Common text field styles
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255,255,255,0.05)',
      color: '#fff',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
      '&.Mui-focused fieldset': { borderColor: accentColor }
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
    '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.5)' }
  };

  // Info icon component with enhanced popover
  const InfoButton = () => {
    if (!param.tooltip) return null;

    return (
      <>
        <IconButton
          size="small"
          onClick={handleInfoClick}
          sx={{
            color: open ? accentColor : 'rgba(255,255,255,0.35)',
            transition: 'all 0.2s ease',
            '&:hover': {
              color: accentColor,
              backgroundColor: `${accentColor}15`
            },
            p: 0.5
          }}
        >
          <InfoIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleInfoClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: '#12121a',
                backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '12px',
                maxWidth: 340,
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.5), 0 0 1px rgba(255,255,255,0.1)',
                mt: 1
              }
            }
          }}
        >
          {/* Header with accent color bar */}
          <Box
            sx={{
              background: `linear-gradient(90deg, ${accentColor}, ${accentColor}99)`,
              px: 2,
              py: 1.5,
              borderBottom: '1px solid rgba(255,255,255,0.05)'
            }}
          >
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75
              }}
            >
              {param.label}
              {param.required && (
                <Chip
                  label="Required"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: '#fff'
                  }}
                />
              )}
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2 }}>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.8)',
                fontSize: '0.8125rem',
                lineHeight: 1.7,
                letterSpacing: '0.01em'
              }}
            >
              {param.tooltip}
            </Typography>

            {/* Min/Max values */}
            {(param.min !== null && param.min !== undefined) || (param.max !== null && param.max !== undefined) ? (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mt: 2,
                  pt: 1.5,
                  borderTop: '1px solid rgba(255,255,255,0.06)'
                }}
              >
                {param.min !== null && param.min !== undefined && (
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      borderRadius: '6px',
                      px: 1.5,
                      py: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75
                    }}
                  >
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Min
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: '0.8125rem', fontWeight: 600 }}>
                      {param.min}
                    </Typography>
                  </Box>
                )}
                {param.max !== null && param.max !== undefined && (
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      borderRadius: '6px',
                      px: 1.5,
                      py: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75
                    }}
                  >
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Max
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: '0.8125rem', fontWeight: 600 }}>
                      {param.max}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : null}
          </Box>
        </Popover>
      </>
    );
  };

  // Build label with required indicator
  const buildLabel = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {param.label}
      {param.required && <span style={{ color: '#ef4444' }}>*</span>}
    </Box>
  );

  // Render based on type
  switch (param.type) {
    case 'checkbox':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 56 }}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(value)}
                onChange={(e) => onChange(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#27B56D' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#27B56D' }
                }}
              />
            }
            label={
              <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9375rem' }}>
                {param.label}
              </Typography>
            }
          />
          <InfoButton />
        </Box>
      );

    case 'number':
      return (
        <TextField
          fullWidth
          type="number"
          label={buildLabel()}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ min: param.min, max: param.max, step: 'any' }}
          InputProps={{
            endAdornment: param.tooltip && (
              <InputAdornment position="end">
                <InfoButton />
              </InputAdornment>
            )
          }}
          sx={textFieldSx}
        />
      );

    case 'symbol':
      return (
        <FormControl fullWidth>
          <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>{buildLabel()}</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            label={param.label}
            endAdornment={param.tooltip && (
              <InputAdornment position="end" sx={{ mr: 2 }}>
                <InfoButton />
              </InputAdornment>
            )}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.05)',
              color: '#fff',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
              '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: '#1e1e2e',
                  border: '1px solid rgba(255,255,255,0.1)',
                  maxHeight: 300,
                  '& .MuiMenuItem-root': {
                    color: '#fff',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                    '&.Mui-selected': { backgroundColor: `${accentColor}30` }
                  }
                }
              }
            }}
          >
            {availableSymbols.length === 0 ? (
              <MenuItem value="" disabled>
                <em>No symbols available</em>
              </MenuItem>
            ) : (
              availableSymbols.map(symbol => (
                <MenuItem key={symbol} value={symbol}>
                  {symbol}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      );

    default:
      return (
        <TextField
          fullWidth
          label={buildLabel()}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          InputProps={{
            endAdornment: param.tooltip && (
              <InputAdornment position="end">
                <InfoButton />
              </InputAdornment>
            )
          }}
          sx={textFieldSx}
        />
      );
  }
};

export default ParamInput;
